import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CAPABILITY_LABELS, fetchModels, fetchMyPlan, estimateCredits } from "../../services/studioService";
import { generateBatch, generateOne, fetchClonedVoices } from "../../services/creativeService";
import VoiceCloneRecorder from "./VoiceCloneRecorder";
import CreditBadge from "./CreditBadge";
import StudioSidebar from "./StudioSidebar";
import ModelSceneCard from "./ModelSceneCard";
import MotionControlPicker, { motionDirective } from "./MotionControlPicker";
import MarketingPresetGallery from "./MarketingPresetGallery";
import ShowcaseGallery from "./ShowcaseGallery";
import VideoEditorPanel from "./VideoEditorPanel";
import HistoryPanel from "./HistoryPanel";
import { fmtCredits, creditLabel } from "../../utils/credits";
import ConnectPlatformBanner from "../Dashboard/components/ConnectPlatformBanner";
import { emitCreditsChanged } from "../Dashboard/components/Navigation/CreditBalancePill";
import { saveCreatives } from "../../services/creativeLibraryService";
import { getRequest, patchRequest } from "../../services/api/apiService";

const PAGE_SIZE = 6; // galerie paginée façon Higgsfield
const RATIOS = ["1:1", "4:5", "9:16", "16:9"];
const COUNTS = [1, 2, 4, 6];

// Capacités « unitaires » (1 génération à la fois, pas de lot de variations) —
// avatar et édition vidéo sont des opérations longues (Veo), la voix off n'a pas de ratio.
const SINGLE_SHOT = ["avatar", "voiceover", "editvideo"];
// Capacités où le motion control (mouvement caméra) a du sens.
const MOTION_CAPABLE = ["text2video", "image2video", "editvideo", "avatar", "ugc"];
// Capacités où la galerie Marketing Studio (formats de pub) est proposée.
const PRESET_CAPABLE = ["text2video", "image2video", "editvideo", "avatar", "ugc"];
const GEMINI_VOICES = ["Aoede", "Charon", "Fenrir", "Kore", "Puck", "Zephyr", "Orus", "Leda"];
const CLOUDINARY_CLOUD = "dxtj7sllk";
const CLOUDINARY_PRESET = "d5rr7y5s";

// Ordre canonique des outils — sert de source pour la validation et le lien par défaut.
const TOOL_KEYS = ["text2image", "text2video", "image2video", "editvideo", "avatar", "voiceover", "ugc", "tryon"];

const CinemaStudio = () => {
  const { tool } = useParams();
  const navigate = useNavigate();
  // Lien direct par outil (/dashboard/cinema-studio/:tool) — capability suit l'URL.
  const [capability, setCapabilityState] = useState(TOOL_KEYS.includes(tool) ? tool : "text2image");
  const setCapability = useCallback((key) => {
    setCapabilityState(key);
    navigate(`/dashboard/cinema-studio/${key}`);
  }, [navigate]);

  // URL invalide/absente → redirige vers le lien canonique de l'outil courant.
  useEffect(() => {
    if (TOOL_KEYS.includes(tool)) { if (tool !== capability) setCapabilityState(tool); }
    else navigate(`/dashboard/cinema-studio/${capability}`, { replace: true });
  }, [tool]); // eslint-disable-line react-hooks/exhaustive-deps

  const [models, setModels] = useState([]);
  const [plan, setPlan] = useState(null);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);

  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("1:1");
  const [count, setCount] = useState(4);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  // Retouche post-génération : image source sélectionnée + prompt de modification.
  const [refineSrc, setRefineSrc] = useState(null);
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refining, setRefining] = useState(false);

  // Capacités unitaires (avatar, voix off, édition vidéo) — état dédié.
  const [voiceText, setVoiceText] = useState("");
  const [voiceName, setVoiceName] = useState(GEMINI_VOICES[0]);
  const [voiceCloneId, setVoiceCloneId] = useState(null);
  const [clonedVoices, setClonedVoices] = useState([]);
  const [showCloneRecorder, setShowCloneRecorder] = useState(false);
  // Frames de guidage Veo (keyframes) — début obligatoire pour l'édition, fin toujours optionnelle.
  const [startFrameUrl, setStartFrameUrl] = useState(null);
  const [endFrameUrl, setEndFrameUrl] = useState(null);
  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  // Motion control — mouvement caméra ajouté au prompt (capacités vidéo uniquement).
  const [motion, setMotion] = useState(null);
  // Mouvement demandé par un préréglage Marketing Studio, appliqué après le
  // changement d'outil éventuel (voir l'effet plus bas).
  const [pendingMotion, setPendingMotion] = useState(null);
  // Incrémenté après chaque génération réussie pour rafraîchir l'historique.
  const [historyKey, setHistoryKey] = useState(0);

  // Charge le palier une fois, puis les modèles à chaque changement de capacité.
  useEffect(() => { fetchMyPlan().then(setPlan).catch(() => {}); }, []);
  useEffect(() => { fetchClonedVoices().then((r) => setClonedVoices(r.voices || [])).catch(() => {}); }, []);

  // Retouche depuis la bibliothèque : ?refine=<url> pré-charge l'image source.
  // ?prompt=<texte> recharge le prompt d'une créa existante dans le composeur
  // (bouton « Réutiliser le prompt » de la bibliothèque / Ads Library) — c'est
  // ce qui permet de repartir d'une créa qui marche pour en décliner d'autres.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = params.get("refine");
    if (url) { setRefineSrc(url); setResults((prev) => (prev.length ? prev : [{ url, type: "image", ok: true }])); }
    const p = params.get("prompt");
    if (p) setPrompt(p);
  }, []);

  // Créa pour une pub Shopify : ?draft=<id> (posé par le tunnel Shopify Ads).
  // On charge le brouillon pour afficher la bannière produit ; chaque résultat
  // généré propose « Utiliser pour ma pub » → attache la créa au brouillon en
  // BD (shopify_ad_drafts) puis revient au tunnel sur ce brouillon.
  const [shopifyDraft, setShopifyDraft] = useState(null);
  const [attaching, setAttaching] = useState(false);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("draft");
    if (!id) return;
    getRequest(`/api/shopify-ad-drafts/${id}`).then((r) => setShopifyDraft(r.draft)).catch(() => {});
  }, []);

  const attachToDraft = async (url, type) => {
    if (!shopifyDraft || attaching) return;
    setAttaching(true);
    try {
      await patchRequest(`/api/shopify-ad-drafts/${shopifyDraft.id}`, {
        creative: { type: type || (isVideo ? "video" : "image"), url, prompt },
      });
      toast.success("Créa attachée à votre pub ✅ — retour au tunnel Shopify.");
      navigate(`/dashboard/shopify-ads?draft=${shopifyDraft.id}`);
    } catch {
      toast.error("Impossible d'attacher la créa au brouillon.");
    } finally {
      setAttaching(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await fetchModels({ capability, ...(plan?.plan?.id ? { plan: plan.plan.id } : {}) });
      setModels(res.models || []);
      setPage(0);
      const first = (res.models || []).find((m) => m.available !== false && m.unlocked !== false);
      setSelected(first || null);
    } catch { toast.error("Chargement des modèles impossible."); }
  }, [capability, plan]);
  useEffect(() => { load(); }, [load]);

  // Estimation du coût à chaque changement (les capacités unitaires facturent toujours ×1).
  useEffect(() => {
    if (!selected) return setCost(null);
    const n = SINGLE_SHOT.includes(capability) ? 1 : count;
    estimateCredits(selected.id, n).then((r) => setCost(r.credits)).catch(() => setCost(selected.credits * n));
  }, [selected, count, capability]);

  const pages = Math.max(1, Math.ceil(models.length / PAGE_SIZE));
  const visible = useMemo(() => models.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE), [models, page]);
  const isVideo = capability !== "text2image" && capability !== "tryon";
  const isSingleShot = SINGLE_SHOT.includes(capability);

  // Change d'onglet → on efface le résultat unitaire précédent (pas la bibliothèque, juste l'aperçu).
  useEffect(() => { setSingleResult(null); setStartFrameUrl(null); setEndFrameUrl(null); setMotion(null); setShowCloneRecorder(false); }, [capability]);

  // Déclaré après l'effet de reset ci-dessus pour passer en second dans le même
  // commit : le mouvement conseillé par le préréglage survit au changement d'outil.
  useEffect(() => {
    if (!pendingMotion) return;
    setMotion(pendingMotion.value);
    setPendingMotion(null);
  }, [pendingMotion, capability]);

  // Marketing Studio — charge le prompt composé dans le composeur. On ne génère
  // pas automatiquement : l'utilisateur relit, ajuste, puis clique sur Générer.
  const applyPreset = ({ prompt: presetPrompt, aspect: presetAspect, motion: presetMotion, capability: presetCapability, label }) => {
    if (presetCapability && TOOL_KEYS.includes(presetCapability) && presetCapability !== capability) {
      setCapability(presetCapability);
    }
    if (presetAspect && RATIOS.includes(presetAspect)) setAspect(presetAspect);
    setPendingMotion({ value: presetMotion || null });
    setPrompt(presetPrompt);
    toast.info(`Format « ${label} » chargé — ajustez puis générez.`);
  };

  // Recharge un élément de l'historique dans le composeur — « Recréer » (identique)
  // ou « Éditer » (même prompt, à ajuster avant de relancer). Ne génère pas seul :
  // l'utilisateur valide en cliquant sur le bouton Générer, pour éviter un débit surprise.
  const loadFromHistory = (item, { edit } = {}) => {
    const modelMatch = models.find((m) => m.id === item.model_id);
    if (modelMatch && modelMatch.unlocked !== false && modelMatch.available !== false) {
      setSelected(modelMatch);
    } else if (modelMatch) {
      toast.warn(`« ${modelMatch.label} » n'est plus disponible sur votre palier — choisissez un autre modèle avant de générer.`);
    }
    if (item.aspect_ratio) setAspect(item.aspect_ratio);
    if (capability === "voiceover") setVoiceText(item.prompt || "");
    else setPrompt(item.prompt || "");
    setStartFrameUrl(item.start_frame_url || null);
    setEndFrameUrl(item.end_frame_url || null);
    toast.info(edit ? "Chargé dans le composeur — ajustez puis générez." : "Chargé à l'identique — cliquez sur Générer pour recréer.");
  };

  // Ajoute le mouvement caméra choisi (motion control) au prompt, pour les capacités vidéo.
  const withMotion = useCallback((p) => (
    motion && MOTION_CAPABLE.includes(capability) ? `${p} — ${motionDirective(motion)}` : p
  ), [motion, capability]);

  const generate = async () => {
    if (!prompt.trim()) return toast.warn("Décrivez ce que vous voulez créer.");
    if (!selected) return toast.warn("Choisissez un modèle.");
    // Garde-fou côté client : on prévient avant l'aller-retour serveur.
    if (cost != null && plan?.credits != null && plan.credits < cost) {
      return toast.warn(`Il vous faut ${creditLabel(cost)} — solde : ${fmtCredits(plan.credits)}.`);
    }
    setLoading(true); setResults([]);
    try {
      const res = await generateBatch({
        prompt: withMotion(prompt.trim()), count, aspectRatio: aspect,
        type: isVideo ? "video" : "image", model: selected.id,
      });
      const ok = (res?.results || []).filter((r) => r.ok && r.url);
      setResults(ok);
      if (ok.length) {
        // Le serveur ne débite que les créas réellement produites.
        toast.success(`${ok.length} créa(s) · ${creditLabel(res.creditsCharged ?? cost)} débité(s)`);
        if (res.balance != null) { setPlan((p) => (p ? { ...p, credits: res.balance } : p)); emitCreditsChanged(res.balance); }
        // Sauvegarde dans la bibliothèque réutilisable (best-effort).
        saveCreatives(ok.map((r) => ({ url: r.url, type: r.type || (isVideo ? "video" : "image"),
          prompt: prompt.trim(), model: selected?.id, aspectRatio: aspect, source: "studio" }))).catch(() => {});
        setHistoryKey((k) => k + 1);
      } else toast.error("Aucune créa générée — aucun crédit débité.");
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data;
      toast.error(s === 402
        ? `Crédits insuffisants : il manque ${fmtCredits(d?.missing ?? 0)} crédit(s). Rechargez dans Crédits.`
        : (d?.message || "Génération impossible."));
    } finally { setLoading(false); }
  };

  // Retouche : régénère une créa à partir de l'image source + une instruction
  // de modification (image-to-image quand le modèle le supporte, sinon prompt enrichi).
  const refine = async () => {
    if (!refinePrompt.trim()) return toast.warn("Décrivez la modification souhaitée.");
    setRefining(true);
    try {
      const res = await generateBatch({
        prompt: `${prompt.trim() ? prompt.trim() + " — " : ""}modification: ${refinePrompt.trim()}`,
        count: 1, aspectRatio: aspect, type: "image",
        model: selected?.id, sourceImage: refineSrc,
      });
      const ok = (res?.results || []).filter((r) => r.ok && r.url);
      if (ok.length) {
        setResults((prev) => [...ok, ...prev]); // la variante retouchée en tête
        toast.success(`Retouche générée · ${creditLabel(res.creditsCharged ?? selected?.credits ?? 0.1)} débité(s)`);
        if (res.balance != null) { setPlan((p) => (p ? { ...p, credits: res.balance } : p)); emitCreditsChanged(res.balance); }
        saveCreatives(ok.map((r) => ({ url: r.url, type: "image",
          prompt: refinePrompt.trim(), model: selected?.id, aspectRatio: aspect, source: "refine" }))).catch(() => {});
        setHistoryKey((k) => k + 1);
        setRefineSrc(null); setRefinePrompt("");
      } else toast.error("Retouche impossible.");
    } catch (e) {
      const d = e?.response?.data;
      toast.error(e?.response?.status === 402
        ? `Crédits insuffisants : il manque ${fmtCredits(d?.missing ?? 0)}.`
        : (d?.message || "Retouche impossible."));
    } finally { setRefining(false); }
  };

  // Upload direct (avatar) — frame de fin optionnelle, sans passer par l'éditeur vidéo.
  const handleEndFrameUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("upload_preset", CLOUDINARY_PRESET);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error(data?.error?.message || "upload échoué");
      setEndFrameUrl(data.secure_url);
    } catch (err) {
      toast.error(err.message || "Import de l'image impossible.");
    }
  };

  // Génération unitaire — avatar (portrait + animation), voix off (TTS), édition vidéo (frame + instruction).
  const generateSingle = async () => {
    if (!selected) return toast.warn("Choisissez un modèle.");
    if (capability === "voiceover" && !voiceText.trim()) return toast.warn("Écrivez le texte à lire.");
    if (capability !== "voiceover" && !prompt.trim()) return toast.warn("Décrivez ce que vous voulez créer.");
    if (capability === "editvideo" && !startFrameUrl) return toast.warn("Capturez ou importez une frame de début.");
    if (cost != null && plan?.credits != null && plan.credits < cost) {
      return toast.warn(`Il vous faut ${creditLabel(cost)} — solde : ${fmtCredits(plan.credits)}.`);
    }
    setSingleLoading(true); setSingleResult(null);
    try {
      const type = capability === "editvideo" ? "edit-video" : capability; // 'avatar' | 'voiceover'
      const usesFrames = capability === "editvideo" || capability === "avatar";
      const res = await generateOne({
        type,
        prompt: capability !== "voiceover" ? withMotion(prompt.trim()) : undefined,
        aspectRatio: aspect,
        imageUrl: capability === "editvideo" ? startFrameUrl : undefined,
        endImageUrl: usesFrames ? (endFrameUrl || undefined) : undefined,
        text: capability === "voiceover" ? voiceText.trim() : undefined,
        voice: capability === "voiceover" && !voiceCloneId ? voiceName : undefined,
        voiceCloneId: capability === "voiceover" ? voiceCloneId || undefined : undefined,
        model: selected.id,
      });
      const kind = capability === "voiceover" ? "audio" : "video";
      const result = { url: res.url, imageUrl: res.imageUrl, type: kind };
      setSingleResult(result);
      toast.success(`Créa générée · ${creditLabel(res.creditsCharged ?? cost)} débité(s)`);
      if (res.balance != null) { setPlan((p) => (p ? { ...p, credits: res.balance } : p)); emitCreditsChanged(res.balance); }
      saveCreatives([{ url: res.url, type: kind, prompt: capability === "voiceover" ? voiceText.trim() : prompt.trim(),
        model: selected.id, aspectRatio: aspect, source: "studio",
        startFrameUrl: res.startFrameUrl || undefined, endFrameUrl: res.endFrameUrl || undefined }]).catch(() => {});
      setHistoryKey((k) => k + 1);
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data;
      toast.error(s === 402
        ? `Crédits insuffisants : il manque ${fmtCredits(d?.missing ?? 0)} crédit(s). Rechargez dans Crédits.`
        : (d?.message || "Génération impossible."));
    } finally { setSingleLoading(false); }
  };

  return (
    <div className="p-2 max-w-7xl">
      <ConnectPlatformBanner moduleLabel="Cinema Studio" accent="fuchsia" />

      {/* Bannière « créa pour ma pub Shopify » — visible pendant tout le détour */}
      {shopifyDraft && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-4 flex items-center gap-3 flex-wrap">
          {shopifyDraft.product?.image_url && (
            <img src={shopifyDraft.product.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-emerald-200 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-[180px]">
            <p className="text-sm font-bold text-emerald-900">🛒 Créa pour votre pub Shopify</p>
            <p className="text-xs text-emerald-700 truncate">
              {shopifyDraft.product?.title} · {shopifyDraft.platform} — générez votre visuel puis cliquez « Utiliser pour ma pub » sur le résultat.
            </p>
          </div>
          <button
            onClick={() => navigate(`/dashboard/shopify-ads?draft=${shopifyDraft.id}`)}
            className="text-xs font-semibold text-emerald-800 border border-emerald-300 rounded-xl px-3.5 py-2 hover:bg-emerald-100 transition flex-shrink-0"
          >
            ← Revenir à ma pub sans créa
          </button>
        </div>
      )}

      {/* Hero — bannière dégradée animée façon Higgsfield */}
      <div data-tour="studio-hero" className="relative overflow-hidden rounded-2xl mb-5 p-6 bg-gradient-to-br from-[#181433] via-[#3b2d78] to-[#7C3AED] bg-200 animate-aurora shadow-lg">
        {/* Vidéo d'arrière-plan façon Higgsfield — fichier léger (~1,6 Mo),
            en boucle muette derrière un voile pour garder le texte lisible. */}
        <video
          src="/videos/showcase-hero.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-35 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#181433]/85 via-[#3b2d78]/60 to-[#7C3AED]/50" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.18),transparent_55%)]" />
        <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-fuchsia-500/20 blur-2xl animate-float-slow" />
        <div className="relative z-10 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">🎬 Cinema Studio</h2>
            <p className="text-sm text-indigo-100/80 mt-1 max-w-xl">
              Image, vidéo, avatar, voix off et édition — 1 vidéo = 1 crédit, 1 image = 0,1 crédit.
              <span className="text-emerald-300 font-medium"> Création de pub et A/B testing gratuits.</span>
            </p>
          </div>
          {plan && (
            <div className="flex items-center gap-2">
              <CreditBadge balance={plan.credits} pending={(loading || singleLoading) ? cost || 0 : 0} />
              <span className="text-xs font-semibold bg-white/15 text-white px-3 py-1.5 rounded-full backdrop-blur">{plan.plan?.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Vitrine Marketing Studio — vidéos d'exemple + leurs prompts */}
      <ShowcaseGallery onUse={applyPreset} />

      {/* Rail latéral (outils + historique) + contenu */}
      <div className="flex flex-col md:flex-row gap-5 items-start">
        <div className="w-full md:w-56 shrink-0 flex flex-col">
          <StudioSidebar capabilities={Object.entries(CAPABILITY_LABELS)} capability={capability} onSelect={setCapability} />
          <HistoryPanel
            capability={capability}
            refreshKey={historyKey}
            onRecreate={(item) => loadFromHistory(item)}
            onEdit={(item) => loadFromHistory(item, { edit: true })}
          />
        </div>

        <div className="flex-1 w-full min-w-0">
          {/* Galerie de modèles — scènes animées façon Higgsfield */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {visible.map((m) => <ModelSceneCard key={m.id} model={m} selected={selected?.id === m.id} onSelect={setSelected} />)}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40">←</button>
              {Array.from({ length: pages }).map((_, i) => (
                <button key={i} onClick={() => setPage(i)}
                  className={`w-8 h-8 rounded-lg text-sm font-semibold ${page === i ? "bg-[#221C51] text-white" : "border border-gray-200 text-gray-600"}`}>{i + 1}</button>
              ))}
              <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page === pages - 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40">→</button>
            </div>
          )}

          {/* Marketing Studio — formats de pub prêts à l'emploi (remplit le composeur) */}
          {PRESET_CAPABLE.includes(capability) && (
            <MarketingPresetGallery onApply={applyPreset} collapsedByDefault={isSingleShot} />
          )}

          {/* Composeur — génération par lot (image / vidéo / animation / UGC / try-on) */}
          {!isSingleShot && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mt-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-[#221C51]">Créer avec {selected?.label || "—"}</p>
              {cost != null && (
                <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full">
                  ≈ {creditLabel(cost)}
                  {plan?.credits != null && <span className="opacity-60"> · reste {fmtCredits(Math.max(0, plan.credits - cost))}</span>}
                </span>
              )}
            </div>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              placeholder={isVideo ? "Décrivez la scène : mouvement, ambiance, produit…" : "Décrivez le visuel : produit, style, fond…"}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51] resize-none" />
            {MOTION_CAPABLE.includes(capability) && (
              <div className="mt-4"><MotionControlPicker value={motion} onChange={setMotion} /></div>
            )}
            <div className="flex flex-wrap gap-6 mt-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">Format</p>
                <div className="flex gap-2">
                  {RATIOS.map((r) => (
                    <button key={r} onClick={() => setAspect(r)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${aspect === r ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600"}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">Variations</p>
                <div className="flex gap-2">
                  {COUNTS.map((c) => (
                    <button key={c} onClick={() => setCount(c)} className={`w-9 h-9 rounded-lg text-sm font-medium border ${count === c ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600"}`}>{c}</button>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={generate} disabled={loading || !selected}
              className="mt-5 px-6 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2e265e] disabled:opacity-50">
              {loading ? `Génération de ${count}…` : `Générer ${count} ${isVideo ? "vidéo(s)" : "visuel(s)"}`}
            </button>
          </div>
          )}

          {/* Composeur — capacités unitaires (avatar, voix off, édition vidéo) */}
          {isSingleShot && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mt-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-[#221C51]">Créer avec {selected?.label || "—"}</p>
              {cost != null && (
                <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full">
                  ≈ {creditLabel(cost)}
                  {plan?.credits != null && <span className="opacity-60"> · reste {fmtCredits(Math.max(0, plan.credits - cost))}</span>}
                </span>
              )}
            </div>

            {/* Éditeur vidéo — capture les frames début/fin (scrub précis + import) */}
            {capability === "editvideo" && (
              <div className="mb-4">
                <VideoEditorPanel
                  onStartFrameCaptured={setStartFrameUrl}
                  onEndFrameCaptured={setEndFrameUrl}
                  startFrameUrl={startFrameUrl}
                  endFrameUrl={endFrameUrl}
                />
              </div>
            )}

            {/* Avatar — frame de fin optionnelle (pose/expression alternative à interpoler) */}
            {capability === "avatar" && (
              <div className="mb-4 flex items-center gap-3">
                <p className="text-xs font-semibold text-gray-500">Frame de fin (optionnel)</p>
                {endFrameUrl && <img src={endFrameUrl} alt="Frame de fin" className="w-10 h-10 rounded-lg object-cover border border-gray-200" />}
                <label className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:border-gray-400 cursor-pointer">
                  {endFrameUrl ? "Changer" : "Importer"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleEndFrameUpload} />
                </label>
                {endFrameUrl && <button onClick={() => setEndFrameUrl(null)} className="text-xs text-gray-400 hover:text-gray-600">Retirer</button>}
              </div>
            )}

            {capability === "voiceover" ? (
              <>
                <textarea value={voiceText} onChange={(e) => setVoiceText(e.target.value)} rows={3} maxLength={4000}
                  placeholder="Texte à lire par la voix off…"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51] resize-none" />
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Voix</p>
                  <div className="flex gap-2 flex-wrap items-center">
                    {GEMINI_VOICES.map((v) => (
                      <button key={v} onClick={() => { setVoiceName(v); setVoiceCloneId(null); }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${!voiceCloneId && voiceName === v ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600"}`}>{v}</button>
                    ))}
                    {clonedVoices.map((v) => (
                      <button key={v.id} onClick={() => setVoiceCloneId(v.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${voiceCloneId === v.id ? "bg-fuchsia-600 text-white border-fuchsia-600" : "border-fuchsia-200 text-fuchsia-700"}`}>
                        🎙 {v.label}
                      </button>
                    ))}
                    <button onClick={() => setShowCloneRecorder((s) => !s)}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border border-dashed border-gray-300 text-gray-500 hover:border-gray-400">
                      + Cloner ma voix
                    </button>
                  </div>
                </div>

                {showCloneRecorder && (
                  <div className="mt-4">
                    <VoiceCloneRecorder
                      onClose={() => setShowCloneRecorder(false)}
                      onCloned={(v) => {
                        setClonedVoices((prev) => [v, ...prev]);
                        setVoiceCloneId(v.id);
                        setShowCloneRecorder(false);
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
                  placeholder={capability === "avatar" ? "Décrivez le personnage : apparence, style, ambiance…" : "Décrivez la modification : mouvement de caméra, lumière, ambiance…"}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51] resize-none" />
                {MOTION_CAPABLE.includes(capability) && (
                  <div className="mt-4"><MotionControlPicker value={motion} onChange={setMotion} /></div>
                )}
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Format</p>
                  <div className="flex gap-2">
                    {RATIOS.filter((r) => r !== "4:5").map((r) => (
                      <button key={r} onClick={() => setAspect(r)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${aspect === r ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600"}`}>{r}</button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <button onClick={generateSingle} disabled={singleLoading || !selected}
              className="mt-5 px-6 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2e265e] disabled:opacity-50">
              {singleLoading ? "Génération…" : capability === "voiceover" ? "Générer la voix" : capability === "avatar" ? "Générer l'avatar" : "Modifier la vidéo"}
            </button>

            {singleLoading && <p className="text-xs text-gray-400 mt-3">Les opérations vidéo Veo peuvent prendre 1 à 5 minutes — merci de patienter.</p>}

            {!singleLoading && singleResult && (
              <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
                {singleResult.type === "audio" ? (
                  <audio controls src={singleResult.url} className="w-full" />
                ) : (
                  <div className="flex items-center gap-4 flex-wrap">
                    {singleResult.imageUrl && <img src={singleResult.imageUrl} alt="Portrait" className="w-20 h-20 rounded-lg object-cover border border-gray-200" />}
                    <a href={singleResult.url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#221C51] underline">▶ Voir la vidéo générée</a>
                    {shopifyDraft && singleResult.type !== "audio" && (
                      <button
                        onClick={() => attachToDraft(singleResult.url, "video")}
                        disabled={attaching}
                        className="text-xs font-semibold text-white bg-emerald-600 rounded-full px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-50"
                      >
                        🛒 Utiliser pour ma pub
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Résultats (lot) */}
          {!isSingleShot && loading && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
              {Array.from({ length: count }).map((_, i) => <div key={i} className="aspect-square rounded-xl bg-gray-100 animate-pulse" />)}
            </div>
          )}
          {!isSingleShot && !loading && results.length > 0 && (
            <>
              {/* Barre de retouche : apparaît quand on choisit une image à modifier */}
              {refineSrc && (
                <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-2xl p-4 mt-5 flex items-center gap-3 flex-wrap">
                  <img src={refineSrc} alt="À retoucher" className="w-14 h-14 rounded-lg object-cover border border-fuchsia-200" />
                  <input
                    value={refinePrompt}
                    onChange={(e) => setRefinePrompt(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && refine()}
                    autoFocus
                    placeholder="Ex : fond plus lumineux, ajoute un reflet, texte « -20% »…"
                    className="flex-1 min-w-[200px] px-3 py-2.5 text-sm border border-fuchsia-300 rounded-xl focus:outline-none focus:border-fuchsia-500"
                  />
                  <button onClick={refine} disabled={refining}
                    className="px-5 py-2.5 bg-fuchsia-600 text-white text-sm font-semibold rounded-xl hover:bg-fuchsia-700 disabled:opacity-50">
                    {refining ? "Retouche…" : "Appliquer"}
                  </button>
                  <button onClick={() => { setRefineSrc(null); setRefinePrompt(""); }}
                    className="text-sm text-gray-400 hover:text-gray-600 px-2">Annuler</button>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
                {results.map((r, i) => (
                  <div key={i} className="group relative rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition">
                    <a href={r.url} target="_blank" rel="noreferrer" className="block">
                      {r.type === "video" ? (
                        <div className="w-full aspect-square flex flex-col items-center justify-center bg-gray-50 text-[#221C51]">
                          <span className="text-4xl">▶</span><span className="text-xs mt-1 font-medium">Voir la vidéo</span>
                        </div>
                      ) : <img src={r.url} alt={`Créa ${i + 1}`} className="w-full aspect-square object-cover" />}
                    </a>
                    {/* Bouton Modifier — uniquement sur les images */}
                    {r.type !== "video" && (
                      <button
                        onClick={() => { setRefineSrc(r.url); setRefinePrompt(""); }}
                        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white/95 text-fuchsia-700 text-xs font-semibold px-2.5 py-1 rounded-full shadow hover:bg-white"
                      >
                        ✎ Modifier
                      </button>
                    )}
                    {/* Détour pub Shopify : attacher cette créa au brouillon */}
                    {shopifyDraft && (
                      <button
                        onClick={() => attachToDraft(r.url, r.type)}
                        disabled={attaching}
                        className="absolute bottom-2 left-2 transition bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow hover:bg-emerald-700 disabled:opacity-50"
                      >
                        🛒 Utiliser pour ma pub
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CinemaStudio;
